import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { clearCachedResponses } from '@/utils/globalFetchDeduplicator';

export interface NetworkRecoveryOptions {
  onRecovered?: (force?: boolean) => void | Promise<void>;
  debounceMs?: number;
  expectedUserId?: string | null;
}

/**
 * Best-effort recovery for browser wake and connectivity restoration.
 * Mount once near the application root.
 */
export function useNetworkRecovery({
  onRecovered,
  debounceMs = 500,
  expectedUserId = null,
}: NetworkRecoveryOptions = {}): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveringRef = useRef(false);
  const expectedUserIdRef = useRef<string | null>(expectedUserId);
  expectedUserIdRef.current = expectedUserId;

  useEffect(() => {
    let disposed = false;

    const recover = async (reason: 'online' | 'visible') => {
      timerRef.current = null;

      if (disposed || !navigator.onLine || recoveringRef.current) return;
      recoveringRef.current = true;

      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.warn('[network-recovery] Session recovery failed', { reason, error });
          return;
        }

        const sessionUserId = data?.session?.user?.id;
        if (!sessionUserId || sessionUserId !== expectedUserIdRef.current) {
          console.warn('[network-recovery] Session recovery skipped', { reason });
          return;
        }

        if (disposed) return;

        clearCachedResponses();

        try {
          await onRecovered?.(true);
        } catch (error) {
          console.warn('[network-recovery] Post-auth recovery failed', error);
        }
      } catch (error) {
        console.warn('[network-recovery] Unexpected recovery failure', error);
      } finally {
        recoveringRef.current = false;
      }
    };

    const scheduleRecovery = (reason: 'online' | 'visible') => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void recover(reason), debounceMs);
    };

    const handleOnline = () => scheduleRecovery('online');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleRecovery('visible');
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [debounceMs, onRecovered]);
}
